import { cachedCall } from '@/services/screenCache'
import { api } from '@/services/api'

/*
 * «Социальная защита» — общие витрины рисков вместо собранной под планшет
 * `/akim/social-protection/dashboard` (решение 29.09.2026: один источник с
 * сайтом):
 * — `/risk/gbd-fl-person-statistics` — ГБД ФЛ по районам, ежедневный прогон:
 *   пенсионеры, неполные семьи, сироты, ОБПР, население;
 * — `/risk/underage-statistics` — несовершеннолетние по уровню риска, категория
 *   0, ровно как «Активные риски» сайта (`GroupRisks.vue`).
 *
 * ЧЕГО ЗДЕСЬ БОЛЬШЕ НЕТ — общих ручек у этого нет:
 * — «В зоне внимания» — сумма по формуле, в которую входили ЦБДИ и ПМПК;
 * — «Дети · ЦБДИ» (инвалидность) и «АСП» — АСП было константой 18 420;
 * — «Охват, %» — синтетика `min(99, получатели / население + 70)`;
 * — +35 % «семей только с матерью» к неполным семьям: этого поля в общей
 *   витрине нет, поэтому неполных семей 5 983, а не 8 067.
 */

export type SocialProtectionPeriod = 'week' | 'month' | 'quarter'

export type SocialProtectionKpi = {
  id: string
  label: string
  value: number
  previous: number | null
  deltaPercent: number | null
  hint?: string
  lowerIsBetter?: boolean
}

export type SocialProtectionDistrictRow = {
  districtId: number
  districtName: string
  population: number
  pensioners: number
  notFullFamily: number
  orphans: number
  obpr: number
  criticalRisk: number
  highRisk: number
}

export type SocialProtectionDashboard = {
  territory: string
  asOf: string
  previousAsOf: string | null
  sourceNote: string
  kpis: SocialProtectionKpi[]
  dynamics: Array<{ label: string; pensioners: number; notFullFamily: number }>
  categories: Array<{ name: string; value: number }>
  riskLevels: Array<{ name: string; value: number }>
  districts: SocialProtectionDistrictRow[]
}

export type SocialProtectionRegionOption = { id: number | ''; name: string }

/* ─── Ручки ─── */

type PersonDistrict = {
  districtId: number | null
  districtName: string
  gbdRegionIds: number[]
  population: { total: number }
  pensioners: { total: number; men: number; women: number }
  family: { notFullFamily: number }
  orphans: { orphans: number; obpr: number }
}

type PersonStatistics = {
  syncId: number
  completedDate: string
  oblast: PersonDistrict
  districts: PersonDistrict[]
}

type PersonSync = { id: number; completedDate: string; status: string }

type UnderageRow = {
  spRegistryCategoryId: number
  region: { id: number } | null
  under18Count: number
  underageWithRisks: number
  underageWithCriticalRisks: number
  underageWithHighRisks: number
  underageWithMediumRisks: number
  underageWithLowRisks: number
  underageWithMvdAccounting: number
}

type Paginated<T> = { items: T[]; meta: { pages: number } }

const personStatistics = (syncId?: number) =>
  api.get<PersonStatistics>('/risk/gbd-fl-person-statistics', {
    params: { spRegistryCategoryId: 0, syncId },
  })

/** Все строки последнего прогона. Сайт берёт так же и оставляет категорию 0. */
async function underageRows(): Promise<UnderageRow[]> {
  const first = await api.get<Paginated<UnderageRow>>('/risk/underage-statistics', {
    params: { limit: 200, page: 1 },
  })
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, first.meta.pages - 1) }, (_, i) =>
      api.get<Paginated<UnderageRow>>('/risk/underage-statistics', { params: { limit: 200, page: i + 2 } }),
    ),
  )
  return [first, ...rest].flatMap((p) => p.items ?? []).filter((r) => r.spRegistryCategoryId === 0)
}

/* ─── Справочник районов ─── */

/**
 * Районы — из самой витрины персон: id — районная прокуратура (как было), имя —
 * как в нашем справочнике.
 */
/** Список районов для фильтра — тоже в `screenCache`: иначе повторное открытие экрана делало ради него один запрос. */
export function fetchSocialProtectionRegions(): Promise<SocialProtectionRegionOption[]> {
  return cachedCall('social-protection/regions', fetchSocialProtectionRegionsRaw)
}

async function fetchSocialProtectionRegionsRaw(): Promise<SocialProtectionRegionOption[]> {
  const stat = await personStatistics()
  return [
    { id: '', name: 'Атырауская область' },
    ...stat.districts
      .filter((d): d is PersonDistrict & { districtId: number } => d.districtId != null)
      .map((d) => ({ id: d.districtId, name: d.districtName })),
  ]
}

/* ─── Сборка экрана ─── */

const LOOKBACK_DAYS: Record<SocialProtectionPeriod, number> = { week: 7, month: 30, quarter: 90 }
const DYNAMICS_SYNCS = 12

function pctDelta(now: number, base: number | null): number | null {
  if (base === null || !base) return null
  return Math.round(((now - base) / base) * 1000) / 10
}

function dayOf(iso: string): string {
  return iso.slice(0, 10)
}

function formatDay(iso: string): string {
  const [y, m, d] = dayOf(iso).split('-')
  return `${d}.${m}.${y}`
}

/** Прогон на дату «последний − N дней»: ближайший не позже неё. */
function previousSync(syncs: PersonSync[], unit: SocialProtectionPeriod): PersonSync | null {
  const [latest, ...rest] = syncs
  if (!latest) return null
  const target = Date.parse(latest.completedDate) - LOOKBACK_DAYS[unit] * 86_400_000
  return rest.find((s) => Date.parse(s.completedDate) <= target) ?? rest[rest.length - 1] ?? null
}

async function fetchSocialProtectionDashboardRaw(params: {
  unit: SocialProtectionPeriod
  regionId?: number | null
}): Promise<SocialProtectionDashboard> {
  const regionId = params.regionId ?? null

  const syncs = (await api.get<PersonSync[]>('/risk/gbd-fl-person-statistics/syncs'))
    .filter((s) => s.status === 'COMPLETED')
    .sort((a, b) => b.completedDate.localeCompare(a.completedDate))
  const prevSync = previousSync(syncs, params.unit)
  const dynamicsSyncs = syncs.slice(0, DYNAMICS_SYNCS).reverse()

  const [latest, previous, series, underage] = await Promise.all([
    personStatistics(),
    prevSync ? personStatistics(prevSync.id) : Promise.resolve(null),
    Promise.all(dynamicsSyncs.map((s) => personStatistics(s.id))),
    underageRows(),
  ])

  const scope = (stat: PersonStatistics | null): PersonDistrict | null =>
    !stat ? null : regionId === null ? stat.oblast : (stat.districts.find((d) => d.districtId === regionId) ?? null)

  const curr = scope(latest)
  if (!curr) throw new Error('В витрине ГБД ФЛ нет такого района')
  const prev = scope(previous)

  /* Дети по уровню риска — по регионам ГБД этого района (город — два региона). */
  const underageOf = (district: PersonDistrict) => {
    const ids = new Set(district.gbdRegionIds)
    const rows = underage.filter((r) => r.region && ids.has(r.region.id))
    const sum = (k: keyof UnderageRow) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0)
    return {
      under18: sum('under18Count'),
      critical: sum('underageWithCriticalRisks'),
      high: sum('underageWithHighRisks'),
      medium: sum('underageWithMediumRisks'),
      low: sum('underageWithLowRisks'),
      mvd: sum('underageWithMvdAccounting'),
    }
  }
  const kids = underageOf(curr)

  const kpi = (
    id: string,
    label: string,
    pick: (d: PersonDistrict) => number,
    hint: string,
    lowerIsBetter = true,
  ): SocialProtectionKpi => {
    const value = pick(curr)
    const base = prev ? pick(prev) : null
    return { id, label, value, previous: base, deltaPercent: pctDelta(value, base), hint, lowerIsBetter }
  }

  const districts: SocialProtectionDistrictRow[] = latest.districts
    .filter((d): d is PersonDistrict & { districtId: number } => d.districtId != null)
    .filter((d) => regionId === null || d.districtId === regionId)
    .map((d) => {
      const k = underageOf(d)
      return {
        districtId: d.districtId,
        districtName: d.districtName,
        population: d.population.total,
        pensioners: d.pensioners.total,
        notFullFamily: d.family.notFullFamily,
        orphans: d.orphans.orphans,
        obpr: d.orphans.obpr,
        criticalRisk: k.critical,
        highRisk: k.high,
      }
    })
    .sort((a, b) => b.population - a.population)

  return {
    territory: curr.districtName,
    asOf: formatDay(latest.completedDate),
    previousAsOf: prevSync ? formatDay(prevSync.completedDate) : null,
    sourceNote:
      `ГБД ФЛ, прогон ${formatDay(latest.completedDate)} (/risk/gbd-fl-person-statistics); ` +
      'дети по уровню риска — /risk/underage-statistics, категория 0, как «Активные риски» сайта.',
    kpis: [
      kpi('pensioners', 'Пенсионеры', (d) => d.pensioners.total, `мужчин ${curr.pensioners.men.toLocaleString('ru-RU')} · женщин ${curr.pensioners.women.toLocaleString('ru-RU')}`, false),
      kpi('orphans', 'Сироты', (d) => d.orphans.orphans, `ОБПР — ${curr.orphans.obpr.toLocaleString('ru-RU')}`),
      kpi('families', 'Неполные семьи', (d) => d.family.notFullFamily, 'ГБД ФЛ'),
      {
        // То же число, что «Критичный уровень» в «Активных рисках» сайта.
        id: 'critical',
        label: 'Дети с критическим риском',
        value: kids.critical,
        previous: null,
        deltaPercent: null,
        hint: `высокий — ${kids.high.toLocaleString('ru-RU')} · на учёте в МВД — ${kids.mvd.toLocaleString('ru-RU')} · всего до 18 — ${kids.under18.toLocaleString('ru-RU')}`,
        lowerIsBetter: true,
      },
    ],
    dynamics: series.map((s) => {
      const d = scope(s)
      return {
        label: formatDay(s.completedDate).slice(0, 5),
        pensioners: d?.pensioners.total ?? 0,
        notFullFamily: d?.family.notFullFamily ?? 0,
      }
    }),
    categories: [
      { name: 'Пенсионеры', value: curr.pensioners.total },
      { name: 'Неполные семьи', value: curr.family.notFullFamily },
      { name: 'Сироты', value: curr.orphans.orphans },
      { name: 'ОБПР', value: curr.orphans.obpr },
      { name: 'Дети: критический риск', value: kids.critical },
      { name: 'Дети: высокий риск', value: kids.high },
    ],
    riskLevels: [
      { name: 'Критический', value: kids.critical },
      { name: 'Высокий', value: kids.high },
      { name: 'Средний', value: kids.medium },
      { name: 'Низкий', value: kids.low },
    ],
    districts,
  }
}

/**
 * Экран целиком — в `screenCache` на час: собранная сводка весит килобайты и
 * переживает перезагрузку вкладки, повторное открытие — без запросов. Сырые
 * ответы при этом живут только в памяти (`api.ts`). Кнопка «Обновить» сбрасывает
 * ключ через `invalidateScreenCache`.
 */
export function fetchSocialProtectionDashboard(params: {
  unit: SocialProtectionPeriod
  regionId?: number | null
}): Promise<SocialProtectionDashboard> {
  return cachedCall(`social-protection/dashboard:${params.unit}:${params.regionId ?? 'all'}`, () => fetchSocialProtectionDashboardRaw(params))
}
